import type { ParagraphInfo } from './proseAnalysis'

// Recasts prose as a screenplay: dialogue under the name of whoever speaks
// it, narration as action lines, headings as scene headings. Speakers come
// from dialogue tags ("Mara said", "said the guard"), from action beats that
// name a character, and, failing both, from the back-and-forth of the
// conversation — those guesses are marked as inferred.

export type ScreenplayBlock =
  | { type: 'scene'; text: string; pos: number }
  | { type: 'transition'; pos: number }
  | { type: 'action'; text: string; pos: number }
  | {
      type: 'dialogue'
      speaker: string
      /** No tag or beat named the speaker; guessed from the conversation. */
      inferred: boolean
      parenthetical: string
      text: string
      pos: number
    }

const SPEECH_VERBS = [
  'said', 'says', 'say', 'asked', 'asks', 'ask', 'replied', 'replies', 'answered', 'answers', 'added', 'adds',
  'continued', 'continues', 'told', 'tells', 'whispered', 'whispers', 'shouted', 'shouts', 'yelled', 'yells',
  'muttered', 'mutters', 'murmured', 'murmurs', 'called', 'calls', 'cried', 'cries', 'snapped', 'snaps',
  'hissed', 'hisses', 'growled', 'growls', 'sighed', 'sighs', 'laughed', 'laughs', 'insisted', 'insists',
  'explained', 'explains', 'began', 'begins', 'offered', 'offers', 'admitted', 'admits', 'agreed', 'agrees',
  'demanded', 'demands', 'breathed', 'breathes', 'repeated', 'repeats', 'protested', 'protests', 'warned',
  'warns', 'screamed', 'screams', 'mumbled', 'mumbles', 'stammered', 'stammers', 'announced', 'announces',
  'interrupted', 'interrupts', 'pleaded', 'pleads', 'called out', 'went on', 'observed', 'noted', 'suggested',
  'wondered', 'retorted', 'countered', 'managed', 'finished', 'corrected',
]
/** Tags that only say someone spoke; anything else becomes a parenthetical. */
const NEUTRAL_VERBS = new Set(['said', 'says', 'say', 'asked', 'asks', 'ask', 'replied', 'replies', 'answered', 'answers', 'told', 'tells', 'added', 'adds', 'continued', 'continues', 'went on'])
const VERB = `(?:${SPEECH_VERBS.map((v) => v.replace(' ', '\\s+')).join('|')})`
const NAME = `(\\p{Lu}[\\p{L}'’\\-]+(?:\\s+\\p{Lu}[\\p{L}'’\\-]+)?)`
const PRONOUN = `(I|he|she|they|we|He|She|They|We)`
const NOUN_PHRASE = `((?:the|a|an|his|her|their|my|our|The|A|An|His|Her|Their|My|Our)\\s+(?:[\\p{Ll}\\-]+\\s+){0,2}[\\p{Ll}\\-]+)`

const NAME_BEFORE_VERB = new RegExp(`${NAME}\\s+${VERB}\\b`, 'u')
const VERB_BEFORE_NAME = new RegExp(`\\b${VERB}\\s+${NAME}`, 'u')
const PHRASE_BEFORE_VERB = new RegExp(`${NOUN_PHRASE}\\s+${VERB}\\b`, 'u')
const VERB_BEFORE_PHRASE = new RegExp(`\\b${VERB}\\s+${NOUN_PHRASE}`, 'u')
const PRONOUN_BEFORE_VERB = new RegExp(`\\b${PRONOUN}\\s+${VERB}\\b`, 'u')
const VERB_BEFORE_PRONOUN = new RegExp(`\\b${VERB}\\s+${PRONOUN}\\b`, 'u')
const ANY_VERB = new RegExp(`\\b(${VERB})\\b`, 'iu')
const ADVERB = /\b(\p{L}{3,}ly)\b/u

/** Capitalized words that are not names even when they appear mid-sentence. */
export const NOT_NAMES = new Set([
  'I', 'The', 'A', 'An', 'He', 'She', 'They', 'We', 'You', 'It', 'His', 'Her', 'Their', 'Our', 'My', 'Your', 'Its',
  'This', 'That', 'These', 'Those', 'Then', 'There', 'Here', 'When', 'While', 'As', 'But', 'And', 'So', 'Or', 'If',
  'What', 'Why', 'How', 'Who', 'Where', 'Which', 'Yes', 'No', 'Oh', 'Well', 'Now', 'Not', 'God', 'Mr', 'Mrs', 'Ms',
  'Dr', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'January', 'February',
  'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'OK', 'Okay',
  'After', 'Before', 'Still', 'Just', 'Even', 'All', 'One', 'Two', 'Some', 'Every', 'Each', 'At', 'In', 'On', 'For',
])

interface Segment {
  kind: 'narration' | 'dialogue'
  text: string
  start: number
}

/**
 * Splits a paragraph into narration and quoted dialogue. The returned `open`
 * says whether the paragraph ends inside a quotation that runs on into the
 * next one.
 */
export function splitQuotes(text: string): { segments: Segment[]; open: boolean } {
  const segments: Segment[] = []
  // A quotation that runs on reopens with a quotation mark; without one, the
  // earlier quotation was simply never closed, so this paragraph starts fresh.
  let inQuote = false
  let start = 0
  const flush = (end: number) => {
    const piece = text.slice(start, end)
    if (piece.trim()) segments.push({ kind: inQuote ? 'dialogue' : 'narration', text: piece, start })
  }
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    const opens = ch === '“' || (ch === '"' && !inQuote)
    const closes = ch === '”' || (ch === '"' && inQuote)
    if (opens && !inQuote) {
      flush(i)
      inQuote = true
      start = i + 1
    } else if (closes && inQuote) {
      flush(i)
      inQuote = false
      start = i + 1
    }
  }
  flush(text.length)
  return { segments, open: inQuote }
}

function titleCase(name: string): string {
  return name.replace(/\s+/g, ' ').trim().toUpperCase()
}

/** Names the prose uses for characters: tagged speakers, and capitalized words met mid-sentence. */
export function collectCharacterNames(paragraphs: ParagraphInfo[]): Set<string> {
  const names = new Set(taggedSpeakerNames(paragraphs))
  const midSentence = new Map<string, number>()
  for (const paragraph of paragraphs) {
    if (paragraph.kind !== 'paragraph') continue
    for (const word of midSentenceCapitals(paragraph.text)) midSentence.set(word, (midSentence.get(word) ?? 0) + 1)
  }
  for (const [word, count] of midSentence) if (count >= 2) names.add(word)
  return names
}

/** Names that a dialogue tag gives a line to ("Mara said", "said Jonas"). */
export function taggedSpeakerNames(paragraphs: ParagraphInfo[]): string[] {
  const names = new Set<string>()
  for (const paragraph of paragraphs) {
    if (paragraph.kind !== 'paragraph') continue
    for (const re of [NAME_BEFORE_VERB, VERB_BEFORE_NAME]) {
      for (const match of paragraph.text.matchAll(new RegExp(re.source, 'gu'))) {
        const name = cleanName(match[1])
        if (name) names.add(name)
      }
    }
  }
  return [...names]
}

/** Capitalized words met mid-sentence, where a capital usually means a name. */
export function midSentenceCapitals(text: string): string[] {
  const words: string[] = []
  for (const match of text.matchAll(/(?<=[\p{Ll},;]\s+)(\p{Lu}[\p{Ll}'’\-]+)/gu)) {
    const word = match[1].replace(/['’]s$/, '')
    if (!NOT_NAMES.has(word)) words.push(word)
  }
  return words
}

/** A matched name without leading capitalized non-names ("Then Mara" → "Mara"). */
function cleanName(raw: string): string | null {
  const words = raw.split(/\s+/)
  while (words.length && NOT_NAMES.has(words[0])) words.shift()
  return words.length ? words.join(' ') : null
}

const PHRASE_STOPWORDS = /\b(and|but|then|or|to|of|in|on|at|with|from|into|as|while)\b/

interface Attribution {
  speaker: string | null
  pronoun: string | null
  verb: string | null
  /** The tag's own text, so it can be taken out of the action line. */
  tag: string | null
}

function findAttribution(narration: string): Attribution {
  const tries: [RegExp, 'name' | 'phrase' | 'pronoun'][] = [
    [NAME_BEFORE_VERB, 'name'],
    [VERB_BEFORE_NAME, 'name'],
    [PHRASE_BEFORE_VERB, 'phrase'],
    [VERB_BEFORE_PHRASE, 'phrase'],
    [PRONOUN_BEFORE_VERB, 'pronoun'],
    [VERB_BEFORE_PRONOUN, 'pronoun'],
  ]
  for (const [re, kind] of tries) {
    const match = narration.match(re)
    if (!match) continue
    const who = kind === 'name' ? cleanName(match[1]) : match[1]
    if (!who) continue
    if (kind === 'phrase' && PHRASE_STOPWORDS.test(who)) continue
    const verb = match[0].match(ANY_VERB)?.[1]?.toLowerCase() ?? null
    return kind === 'pronoun'
      ? { speaker: null, pronoun: who, verb, tag: match[0] }
      : { speaker: who, pronoun: null, verb, tag: match[0] }
  }
  return { speaker: null, pronoun: null, verb: null, tag: null }
}

/** Narration minus a dialogue tag, or '' when nothing but the tag remains. */
function stripTag(narration: string, tag: string | null): string {
  let rest = tag ? narration.replace(tag, ' ') : narration
  const adverb = tag ? rest.match(new RegExp(`^[\\s,]*${ADVERB.source}`, 'u')) : null
  if (adverb) rest = rest.slice(adverb[0].length)
  rest = rest.replace(/\s+/g, ' ').replace(/^[\s,.;:—–-]+/, '').replace(/[\s,;:—–-]+$/, '').trim()
  if ((rest.match(/[\p{L}\p{N}]+/gu)?.length ?? 0) < 2) return ''
  if (!/[.!?…]$/.test(rest)) rest += '.'
  return rest[0].toUpperCase() + rest.slice(1)
}

/** A character the writer has named in the Reference Manager: a page title and its aliases. */
export interface KnownCharacter {
  name: string
  aliases: string[]
}

function normalizeName(value: string): string {
  return value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase()
}

/**
 * Builds the screenplay. Known characters make speakers more certain: their
 * names and aliases count as names even when the prose mentions them once,
 * and every alias is shown under the one name ("Dr. Holt" lines go under MARA
 * when Mara's page lists that alias).
 */
export function buildScreenplay(paragraphs: ParagraphInfo[], characters: KnownCharacter[] = []): ScreenplayBlock[] {
  const names = collectCharacterNames(paragraphs)
  const pageText = paragraphs.map((p) => p.text).join('\n')
  const canonical = new Map<string, string>()
  for (const character of characters) {
    for (const term of [character.name, ...character.aliases]) {
      const trimmed = term.trim()
      if (trimmed.length < 2) continue
      canonical.set(normalizeName(trimmed), titleCase(character.name))
      // Tags drop a title ("said Holt" for Dr. Holt), so the bare name counts too.
      const untitled = trimmed.replace(/^(?:Mr|Mrs|Ms|Mx|Dr|Prof|Sir|Dame|Lady|Lord|Capt|Captain|Sgt|Lt|Col|Gen)\.?\s+/i, '')
      if (untitled !== trimmed && untitled.length >= 2) canonical.set(normalizeName(untitled), titleCase(character.name))
      // Only names written on this page, capitalized as a name would be.
      if (/^\p{Lu}/u.test(trimmed) && pageText.includes(trimmed)) names.add(trimmed)
    }
  }
  const resolve = (speaker: string) => canonical.get(normalizeName(speaker)) ?? speaker
  const blocks: ScreenplayBlock[] = []
  /** Speakers in order of their latest line, most recent last. */
  let recent: string[] = []
  let quoteOpen = false
  let carriedSpeaker: string | null = null

  const remember = (speaker: string) => {
    recent = recent.filter((s) => s !== speaker)
    recent.push(speaker)
    if (recent.length > 4) recent.shift()
  }

  for (const paragraph of paragraphs) {
    if (paragraph.kind === 'heading') {
      if (paragraph.text.trim()) blocks.push({ type: 'scene', text: paragraph.text.trim().toUpperCase(), pos: paragraph.from })
      recent = []
      quoteOpen = false
      continue
    }
    if (paragraph.kind === 'rule') {
      blocks.push({ type: 'transition', pos: paragraph.from })
      recent = []
      quoteOpen = false
      continue
    }
    if (!paragraph.text.trim()) continue

    const wasOpen = quoteOpen
    const { segments, open } = splitQuotes(paragraph.text)
    quoteOpen = open
    const dialogue = segments.filter((s) => s.kind === 'dialogue')
    if (!dialogue.length) {
      blocks.push({ type: 'action', text: paragraph.text.trim(), pos: paragraph.from })
      continue
    }

    const narration = segments.filter((s) => s.kind === 'narration').map((s) => s.text).join(' ')
    const attribution = findAttribution(narration)
    let speaker = attribution.speaker ? titleCase(attribution.speaker) : null
    let inferred = false

    if (!speaker) {
      // An action beat in the same paragraph usually belongs to the speaker.
      for (const name of names) {
        if (new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'u').test(narration)) {
          speaker = titleCase(name)
          break
        }
      }
    }
    if (!speaker && attribution.pronoun && /^(I|we)$/i.test(attribution.pronoun)) speaker = 'NARRATOR'
    if (!speaker && wasOpen && carriedSpeaker && /^\s*["“]/.test(paragraph.text)) speaker = carriedSpeaker
    if (!speaker && recent.length >= 2) {
      speaker = recent[recent.length - 2]
      inferred = true
    }
    if (!speaker && attribution.pronoun) {
      speaker = attribution.pronoun.toUpperCase()
      inferred = true
    }
    if (!speaker) {
      speaker = '?'
      inferred = true
    }
    speaker = resolve(speaker)

    let parenthetical = ''
    if (attribution.tag) {
      const adverb = narration.slice(narration.indexOf(attribution.tag) + attribution.tag.length).match(new RegExp(`^[\\s,]*${ADVERB.source}`, 'u'))?.[1]
      if (adverb) parenthetical = adverb.toLowerCase()
      else if (attribution.verb && !NEUTRAL_VERBS.has(attribution.verb)) parenthetical = attribution.verb
    }

    // Narration before the first line of dialogue sets the scene; the rest follows it.
    const firstDialogue = segments.indexOf(dialogue[0])
    const before = segments.slice(0, firstDialogue).filter((s) => s.kind === 'narration').map((s) => s.text).join(' ')
    const after = segments.slice(firstDialogue).filter((s) => s.kind === 'narration').map((s) => s.text).join(' ')
    const tagInBefore = attribution.tag && before.includes(attribution.tag) ? attribution.tag : null
    const tagInAfter = attribution.tag && !tagInBefore ? attribution.tag : null

    const leading = stripTag(before, tagInBefore)
    if (leading) blocks.push({ type: 'action', text: leading, pos: paragraph.from })

    const spoken = dialogue.map((s) => s.text.trim()).join(' ').replace(/\s+/g, ' ')
    blocks.push({
      type: 'dialogue',
      speaker,
      inferred,
      parenthetical,
      text: spoken,
      pos: paragraph.from + dialogue[0].start,
    })

    const trailing = stripTag(after, tagInAfter)
    if (trailing) blocks.push({ type: 'action', text: trailing, pos: paragraph.from + (segments[firstDialogue + 1]?.start ?? 0) })

    if (speaker !== '?') remember(speaker)
    carriedSpeaker = open ? speaker : null
  }

  return blocks
}
